import {WhitelabelLanguageDetector} from "../../../../common/ts/core/WhitelabelLanguageDetector";
import {EventBus} from "../../../../common/ts/events/EventBus";
import {ApiGeoResponse, AutocompleteResponseEvent, GenerateTextSuggestionElementData} from "../../../../common/ts/events/Events";
import dom from "../../../../common/ts/core/dom";
import {FILTER_NAMES} from "../../../../common/ts/serp/filters/constants";
import location from "../../../../common/ts/core/location";
import {FiltersSubmittedEvent} from "./events";
import tracking from "../../../../common/ts/core/tracking";
import {getFormDataSubmittingInFilterBar} from "./filterFormData";

const textSuggestionCssClass = "text-suggestion";
const textSuggestionTitleCssClass = "text-suggestion-title";
const focusCssClass = "focus";

let eventBus: EventBus;

function init(
    {
        id,
        geoSlug,
        eb = EventBus.getInstance(),
    }: {
        id: string,
        geoSlug: string,
        eb?: any,
    }
) {
    const autocompleteContainer = dom.select(`#${id}`) as HTMLElement;
    if(autocompleteContainer == null) {
        return;
    }
    eventBus = eb;

    const filtersForm = autocompleteContainer?.querySelector("#filters-form-bar") as HTMLFormElement;
    if (!!filtersForm) {
        filtersForm.addEventListener("keypress", (e) => {
            if (e.key === "Enter") {
                e.preventDefault();
            }
        });
    }

    let debounce: NodeJS.Timeout;
    let ctr: AbortController | null;

    hideSuggestions(autocompleteContainer);

    const autocompleteInput = autocompleteContainer?.querySelector("#search-bar") as HTMLInputElement | null;
    if(autocompleteInput != null){
        if(autocompleteInput.value.length > 2) {
            autocompleteInput!.addEventListener('focus', (e)=>{
                handleInputChange(e, geoSlug, autocompleteContainer);
            })
        }

        autocompleteInput!.addEventListener('keydown', (e) => {
            if (e.key === "ArrowDown" || e.key === "ArrowUp") {
                handleKeyUp(e, autocompleteContainer);
                return;
            } else if(e.key === "Enter") {
                e.preventDefault();
            }
            if (debounce) {
                clearTimeout(debounce);
            }
            debounce = setTimeout(async () => {
                if (ctr) {
                    ctr.abort();
                }
                const {httpCall, controller}: HandleChangeResponse = handleInputChange(e, geoSlug, autocompleteContainer);
                ctr = controller;
                await httpCall();
            }, 500);
        });
    }

    addListenerEventForAutocompleteFieldMobile(autocompleteContainer);
    addListenerAutocompleteModalClose(autocompleteContainer);
    moveModalToBodyIfExist(autocompleteContainer);
    registerClickOutside(autocompleteContainer);
}

function registerClickOutside(autocompleteContainer: HTMLElement) {
    window.onclick = function (e: MouseEvent) {
        if (e.target){
            const target = e.target as HTMLElement;
            if (!target.matches('.search-results__options') && target.id != 'search-bar') {
                hideSuggestions(autocompleteContainer);
            }
        }
    }
}

function hideSuggestions(autocompleteContainer: HTMLElement | null) {
    const autocompleteSuggestions = autocompleteContainer?.querySelector("#matched") as HTMLElement | null;
    if(autocompleteSuggestions != null) {
        removeAllChildNodes(autocompleteSuggestions);
        autocompleteSuggestions.style.display = 'none';
    }
}

function showAutocompleteDataList(autocompleteSuggestions: HTMLElement) {
    autocompleteSuggestions.style.display = 'flex';
}

function handleKeyUp(event: KeyboardEvent, autocompleteContainer: HTMLElement) {
    const suggestionsContainer = autocompleteContainer.querySelector("#matched") as HTMLElement;
    const suggestionsElements = Array.from(suggestionsContainer.querySelectorAll(`.${textSuggestionCssClass}:not(.${textSuggestionTitleCssClass})`));

    let suggestionIndex = 0;
    if (suggestionsElements.length > 0) {
        const focusedSuggestion = suggestionsElements.find((suggestion, i) => {
            if (suggestion.classList.contains(focusCssClass)) {
                suggestionIndex = i;
                return true;
            }

            return false;
        });

        if (focusedSuggestion) {
            if (event.key === "ArrowDown") {
                const nextSuggestion = suggestionsElements[suggestionIndex + 1];
                if (nextSuggestion != null && !nextSuggestion!.classList.contains(textSuggestionTitleCssClass)) {
                    nextSuggestion!.classList.add(focusCssClass);
                    focusedSuggestion.classList.remove(focusCssClass);
                }
            } else if (event.key === "ArrowUp") {
                const previousSuggestion = suggestionsElements[suggestionIndex - 1];
                if (previousSuggestion != null && !previousSuggestion!.classList.contains(textSuggestionTitleCssClass)) {
                    previousSuggestion!.classList.add(focusCssClass);
                    focusedSuggestion.classList.remove(focusCssClass);
                }
            } else if (event.key === "Enter") {
                (focusedSuggestion as HTMLElement).click();
            }

            focusedSuggestion.scrollIntoView({block: "nearest"});
        } else {
            suggestionsElements[0].classList.add(focusCssClass);
        }
    }
}

interface HandleChangeResponse {
    controller: AbortController | null;
    httpCall: () => Promise<any>;
}

function handleInputChange(
    e: any,
    geoSlug: any,
    autocompleteContainer: HTMLElement | null,
): HandleChangeResponse {
    const autocompleteSuggestions = autocompleteContainer?.querySelector("#matched") as HTMLElement | null;
    const query: string = e.target.value;
    if (!query.length || query.length < 2) {
        hideSuggestions(autocompleteContainer);
        return {
            controller: null,
            httpCall: () => Promise.resolve(),
        };
    }

    const controller: AbortController = new AbortController();
    const geoValue = geoSlug.replaceAll("-", " ");
    const geoQuery =(!geoValue || (geoValue && query.toLowerCase().indexOf(geoValue.toLowerCase()) < 0)) ? query : geoValue;

    return {
        controller,
        httpCall: () => fetch(
            `${(new WhitelabelLanguageDetector().detectLanguage())}/api/geo?q=${encodeURIComponent(geoQuery)}`,
            {signal: controller.signal}
        ).then(async (res) => {
            const body: ApiGeoResponse[] = await res.json();
            removeAllChildNodes(autocompleteSuggestions);
            if (body.length === 0) {
                hideSuggestions(autocompleteContainer);
                return;
            }

            EventBus.getInstance().emit(new AutocompleteResponseEvent({
                body: body,
                callbacks: {
                    generateTextSuggestionElement: (html: string, data: GenerateTextSuggestionElementData) : void => {
                        const textSuggestionElement = document.createElement('div');
                        textSuggestionElement.innerHTML = html;
                        const node = textSuggestionElement.firstElementChild!;
                        node.addEventListener("click", (e) => {
                            handleClickOnSuggestion(data, e, autocompleteContainer);
                        });
                        if (autocompleteSuggestions != null) {
                            autocompleteSuggestions.appendChild(node);
                        }
                    },
                    insertAdjacentHTML: (html: string) => {
                        if (autocompleteSuggestions != null) {
                            autocompleteSuggestions.insertAdjacentHTML('beforeend', html);
                        }
                    },
                    showAutocompleteDataList: () => {
                        if (autocompleteSuggestions != null) {
                            showAutocompleteDataList(autocompleteSuggestions);
                        }
                    },
                },
                cssClassNames: {
                    textSuggestionCssClass: textSuggestionCssClass,
                    textSuggestionTitleCssClass: textSuggestionTitleCssClass,
                    focusCssClass: focusCssClass,
                }
            }));
        })
    };
}

function handleCloseModal(autocompleteSuggestions: HTMLElement | null) {
    if (autocompleteSuggestions) {
        const autocompleteModal = autocompleteSuggestions.querySelector(".autocomplete-modal") as HTMLElement | null;
        autocompleteModal?.classList.remove("show");
    }

    document.body.style.overflow = 'auto';
}

function moveModalToBodyIfExist(autocompleteContainer: HTMLElement | null) {
    const autocompleteModal = autocompleteContainer?.querySelector(".autocomplete-modal") as HTMLElement | null;
    if (autocompleteModal != null) {
        document.body.appendChild(autocompleteModal);
    }
}

function addListenerEventForAutocompleteFieldMobile(autocompleteElement: HTMLElement) {
    const autocompleteField = autocompleteElement.querySelector("#search-bar") as HTMLInputElement | null;
    const autocompleteModalElement = autocompleteElement.querySelector(".autocomplete-modal") as HTMLElement | null;
    if (!!autocompleteElement && autocompleteModalElement) {
        autocompleteElement.addEventListener('focusin', () => {
            autocompleteModalElement?.classList.add("show");
            autocompleteField!.focus()
            document.body.style.overflow = 'hidden';
        })
    }
}

function addListenerAutocompleteModalClose(autocompleteContainer: HTMLElement | null) {
    const autocompleteModalClose = autocompleteContainer?.querySelector(".close") as HTMLElement | null;
    if (autocompleteModalClose != null) {
        autocompleteModalClose.addEventListener(
            'click',
            () => handleCloseModal(autocompleteContainer),
        )
    }
}

function removeAllChildNodes(parent: HTMLElement | null) {
    if (parent != null) {
        while (parent.firstChild) {
            parent.removeChild(parent.firstChild)
        }
    }
}

function shouldSendForm(e: Event) {
    const target: EventTarget | null = e.target;
    if (target != null) {
        return (target as HTMLElement).closest(".search-results__options.auto") !== null;
    } else {
        return false;
    }
}

function handleClickOnSuggestion(
    data: GenerateTextSuggestionElementData,
    e: Event,
    autocompleteContainer: HTMLElement | null,
) {
    const {
        id,
        name,
        completeText,
        isProject,
    } = data;

    const autocompleteInput = autocompleteContainer?.querySelector("#search-bar") as HTMLInputElement | null;
    if (autocompleteInput != null) {
        autocompleteInput.value = completeText;
    }

    if (isProject) {
        const projectIdHiddenField = document.getElementById("hidden-project-id") as HTMLInputElement | null;
        if (projectIdHiddenField != null) {
            projectIdHiddenField.value = id;
        }

        const projectNameHiddenField = document.getElementById("hidden-project-name") as HTMLInputElement | null;
        if (projectNameHiddenField != null) {
            projectNameHiddenField.value = name;
        }
    } else {
        const geoIdHiddenField = document.getElementById("hidden-geo-id") as HTMLInputElement | null;
        if (geoIdHiddenField != null) {
            geoIdHiddenField.value = id;
        }
    }

    if (shouldSendForm(e)) {
        eventBus.emitMany([
            new FiltersSubmittedEvent({
                formData: {
                    ...(getFormDataSubmittingInFilterBar()),
                    [FILTER_NAMES.GEO_ID]: id,
                    [FILTER_NAMES.GEO_CHILDREN_ID]: null,
                },
                searchParams: new URLSearchParams(location.getQueryParams()),
                type: FiltersSubmittedEvent.FORM_TYPE,
            }),
            new tracking.TrackWebTrackingEvent({
                type: "FiltersUpdated",
                serpUrl: window.location.href,
                filters: { [FILTER_NAMES.LOCATION]: completeText }
            })
        ]);
    } else {
        const autocompleteInputInModal = autocompleteContainer?.querySelector("#autocomplete-mobile-input") as HTMLInputElement | null;
        if (autocompleteInputInModal != null) {
            autocompleteInputInModal.value = completeText;
        }

        const autocompleteInput = autocompleteContainer?.querySelector("#search-bar") as HTMLInputElement | null;
        if (autocompleteInput != null) {
            autocompleteInput.value = completeText;
        }

        hideSuggestions(autocompleteContainer);
        handleCloseModal(autocompleteContainer);
    }
}

export default {
    init
}
